import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateOrganization } from '../../../../slices/organizationSlice';
import { displayAlert } from '../../../../slices/feedbackSlice';
import Paliers from './Paliers';

/**
 * Les réglages d'envoi automatique, au niveau de l'organisation.
 *
 * 🔑 Ils existaient dans l'API et dans l'admin Django depuis le 01/09/2026,
 * nulle part dans l'application. Un réglage qu'on ne peut changer que par un
 * accès d'administration n'est pas un réglage : c'est une faveur.
 *
 * ⚠️ Les deux commutateurs sont INDÉPENDANTS. Prévenir ses membres et se
 * prévenir soi-même sont deux décisions différentes : une bibliothèque peut
 * vouloir suivre ses retards sans jamais écrire à personne.
 */
export default function Notifications({ organization }) {
  const { t } = useTranslation('myAccount');
  const dispatch = useDispatch();
  // ⚠️ L'erreur de validation renvoyée par le serveur, affichée SOUS
  // l'éditeur. Le bandeau global la dit déjà, mais il disparaît : quelqu'un
  // qui corrige sa saisie doit pouvoir relire ce qu'on lui reproche.
  const [erreurPaliers, setErreurPaliers] = useState(null);

  const enregistrer = (champs) => {
    dispatch(updateOrganization({ id: organization.id, ...champs }))
      .unwrap()
      .then(() => dispatch(displayAlert(t('notifications.saved'), 'success')))
    // ⛔ Pas d'alerte d'erreur ici : le thunk en pose déjà une avec le
    // message de l'API. En ajouter une seconde afficherait deux
    // bandeaux pour un seul échec.
      .catch(() => {});
  };

  const commutateur = (champ, libelle, aide) => (
    <Box sx={{ mb: 3 }}>
      <FormControlLabel
        control={(
          <Switch
            checked={Boolean(organization[champ])}
            onChange={(evenement) => enregistrer({ [champ]: evenement.target.checked })}
          />
                )}
        label={t(libelle)}
      />
      {/* ⚠️ « body1 » et non « body2 » : dans ce thème body2 vaut 12 px, et
          le texte d'aide d'un champ 11 px. Ce n'est pas une nuance de goût —
          un texte qui explique ce qu'un commutateur déclenche doit se lire,
          et c'est justement celui qu'on rapetisse par réflexe parce qu'il
          est « secondaire ». La couleur adoucie suffit à le mettre en second. */}
      <Typography component="p" variant="body1" sx={{ color: 'text.secondary', ml: 6 }}>
        {t(aide)}
      </Typography>
    </Box>
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={t('notifications.title')}
        titleTypographyProps={{ variant: 'h3' }}
      />
      <CardContent>
        <Typography component="p" variant="body1" sx={{ mb: 3 }}>
          {t('notifications.description')}
        </Typography>

        {commutateur(
          'member_reminders_enabled',
          'notifications.members',
          'notifications.members_help',
        )}

        {commutateur(
          'librarian_digest_enabled',
          'notifications.librarian',
          'notifications.librarian_help',
        )}

        {/* 🔑 SOUS les deux commutateurs, pas entre eux : la suite vaut pour
            les deux envois. Placée entre les deux, elle se lisait comme un
            réglage du premier — le récapitulatif semblait obéir à autre chose.
            Le récapitulatif dit à la bibliothécaire ce que ses membres ont
            reçu : les deux suivent forcément le même calendrier. */}
        {(organization.member_reminders_enabled
          || organization.librarian_digest_enabled) && (
          <Paliers
            paliers={organization.reminder_schedule_days || []}
            erreur={erreurPaliers}
            onChange={(paliers) => {
              setErreurPaliers(null);
              enregistrer({ reminder_schedule_days: paliers });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

Notifications.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organization: PropTypes.object.isRequired,
};
